import { useState, useEffect } from 'react';

import {
  TableContainer,
  TablePagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  IconButton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { getResources } from '../../../services/ResourcesService';
import { permissionOptions } from '../../../utils/Enums';
import AddEditResourcesModal from './AddEditResourcesModal';
import DeleteResourceModal from './DeleteResourceModal';

function ResourcesTable({ locationId }) {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState({
    add: false,
    edit: false,
    view: false,
    delete: false
  });

  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const hasPermission = (permissionId) => userPermissions.includes(permissionId);

  useEffect(() => {
    fetchResources();
  }, [locationId]);

  const fetchResources = async () => {
    setLoading(true);
    const data = await getResources(locationId);
    setResources(data);
    setLoading(false);
  };

  const handleModal = (type, open, resource = null) => {
    setModalState(prev => ({ ...prev, [type]: open }));
    setSelectedResource(resource);
  };

  const handleAddResource = async (newResource) => {
    setResources([...resources, newResource]);
    handleModal('add', false);
  };

  const handleEditResource = async (updatedResource) => {
    const updatedResources = resources.map(resource => resource.id === updatedResource.id ? updatedResource : resource);
    setResources(updatedResources);
    handleModal('edit', false);
  };

  const handleDeleteProduct = async () => {
    const filteredResources = resources.filter(resource => resource.id !== selectedResource.id);
    setResources(filteredResources);
    handleModal('delete', false);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const showActionsColumn =
    hasPermission(permissionOptions.find(p => p.label === 'UPDATE_RESOURCES').id) ||
    hasPermission(permissionOptions.find(p => p.label === 'DELETE_RESOURCES').id);

  return (
    <>
      <div className="header-container">
        {hasPermission(permissionOptions.find(p => p.label === 'CREATE_RESOURCES').id) && (
          <Button className="right_button_table" variant="outlined" startIcon={getIcon('add')} onClick={() => handleModal('add', true)}>
            {t('add_resources')}
          </Button>
        )}
      </div>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer component={Paper} style={{boxShadow: null}}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 1100 }}>
              <Table stickyHeader aria-label="resources table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '20%' }}>
                      <span className="roboto-regular">{t('name')}</span>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '25%' }}>
                      <span className="roboto-regular">{t('description')}</span>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '10%' }}>
                      <span className="roboto-regular">{t('quantity')}</span>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '15%' }}>
                      <span className="roboto-regular">{t('min_capacity')}</span>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '15%' }}>
                      <span className="roboto-regular">{t('max_capacity')}</span>
                    </TableCell>
                    {showActionsColumn && (
                      <TableCell align="right" sx={{ width: '15%' }}>
                        <span className="roboto-regular">{t('actions')}</span>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box py={1}  justifyContent="center">
                          <CircularProgress size={32} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    resources.map(resource => (
                      <TableRow key={resource.id}>
                        <TableCell><span className="roboto-light">{resource.name}</span></TableCell>
                        <TableCell align="center"><span className="roboto-light">{resource.description}</span></TableCell>
                        <TableCell align="center"><span className="roboto-light">{resource.quantity}</span></TableCell>
                        <TableCell align="center">
                          <span className="roboto-light">{resource.minCapacity !== undefined && resource.minCapacity !== null ? resource.minCapacity : '-'}</span>
                        </TableCell>
                        <TableCell align="center">
                          <span className="roboto-light">{resource.maxCapacity !== undefined && resource.maxCapacity !== null ? resource.maxCapacity : '-'}</span>
                        </TableCell>
                        {showActionsColumn && (
                          <TableCell align="right">
                            {hasPermission(permissionOptions.find(p => p.label === 'UPDATE_RESOURCES').id) && (
                              <IconButton onClick={() => handleModal('edit', true, resource)}>{getIcon('settings')}</IconButton>
                            )}
                            {hasPermission(permissionOptions.find(p => p.label === 'DELETE_RESOURCES').id) && (
                              <IconButton onClick={() => handleModal('delete', true, resource)}>{getIcon('trash')}</IconButton>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={resources.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {(modalState.add || modalState.edit) && (
        <AddEditResourcesModal
          locationId={locationId}
          open={modalState.add || modalState.edit}
          onClose={() => handleModal(modalState.add ? 'add' : 'edit', false)}
          onSave={modalState.add ? handleAddResource : handleEditResource}
          resource={selectedResource}
        />
      )}

      {modalState.delete && (
        <DeleteResourceModal
          open={modalState.delete}
          resourceId={selectedResource.id}
          resourceName={selectedResource.name}
          onClose={() => handleModal('delete', false)}
          onDelete={handleDeleteProduct}
        />
      )}
    </>
  );
}

export default ResourcesTable;
